import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { Alert, AlertTitle, Button, Container, Stack } from '@mui/material'

import CurrencyUtils from '@utils/currency'
import { ICoupon, IPlan } from '@utils/types'

import { alertSx, buttonSx, containerSx, wrapperSx } from './styles'

interface ICouponAlertProps {
  coupon: ICoupon
  standardPlans?: IPlan[]
  onClaimOffer({ plan, coupon }: { plan: IPlan; coupon: ICoupon }): void
}

const CouponAlert = ({
  coupon,
  standardPlans,
  onClaimOffer,
}: ICouponAlertProps) => {
  const { t } = useTranslation()

  const handleOnClaimCouponAlertClick = useCallback(() => {
    const planIdWithCoupon = coupon?.plan?.id

    if (!planIdWithCoupon) {
      return
    }

    const planWithCoupon = standardPlans?.find(
      (plan) => plan.id === planIdWithCoupon
    )

    if (!planWithCoupon) {
      return
    }

    onClaimOffer({ plan: planWithCoupon, coupon })
  }, [coupon, standardPlans, onClaimOffer])

  const getDiscountText = useCallback(() => {
    const amountOff = coupon?.amountOff
    const percentOff = coupon?.percentOff

    if (amountOff) {
      return t('{{amountOff}} off', {
        amountOff: CurrencyUtils.toCurrencyFormat(amountOff),
      })
    }

    if (percentOff) {
      return t('{{percentOff}}% off', {
        percentOff,
      })
    }
  }, [t, coupon?.amountOff, coupon?.percentOff])

  return (
    <Container sx={containerSx}>
      <Stack sx={wrapperSx}>
        <Alert
          data-testid="active-offer-alert"
          severity="success"
          action={
            <Button
              data-testid="claim-offer-button"
              variant="outlined"
              color="success"
              onClick={handleOnClaimCouponAlertClick}
              sx={buttonSx}
            >
              {t('Claim It!')}
            </Button>
          }
          sx={alertSx}
        >
          <AlertTitle>{t('Offer Ready to be Claimed!')}</AlertTitle>
          {t('You have an active offer for')} <b>{coupon?.plan?.alias}</b>{' '}
          {t('plan with')} <b>{getDiscountText()}</b> {t('waiting for you!')}
        </Alert>
      </Stack>
    </Container>
  )
}

export { CouponAlert }
